import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function Login() {
  const [step,    setStep]    = useState('email'); // 'email' | 'otp'
  const [email,   setEmail]   = useState('');
  const [otp,     setOtp]     = useState(['','','','','','']);
  const [loading, setLoading] = useState(false);
  const [resendCD,setResendCD]= useState(0);
  const { login } = useAuth();
  const navigate  = useNavigate();
  const inputs    = useRef([]);

  // Countdown timer for resend
  const startCountdown = () => {
    setResendCD(30);
    const t = setInterval(() => {
      setResendCD(c => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; });
    }, 1000);
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/request-otp', { email });
      toast.success(`OTP sent to ${email}`);
      setStep('otp');
      setOtp(['','','','','','']);
      startCountdown();
      setTimeout(() => inputs.current[0]?.focus(), 100);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP.');
    } finally { setLoading(false); }
  };

  const handleOTPInput = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[idx] = val.slice(-1);
    setOtp(next);
    if (val && idx < 5) inputs.current[idx + 1]?.focus();
    // Auto-submit when all 6 filled
    if (next.every(d => d) && next.join('').length === 6) {
      handleVerifyOTP(next.join(''));
    }
  };

  const handleOTPKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };

  const handleVerifyOTP = async (otpVal) => {
    const code = otpVal || otp.join('');
    if (code.length !== 6) { toast.error('Enter the 6-digit OTP.'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-otp', { email, otp: code });
      if (!['hr','manager'].includes(data.user.role)) throw new Error('Access denied. HR or Manager accounts only.');
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      // Trigger auth context update
      window.location.href = '/';
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Invalid OTP.');
      setOtp(['','','','','','']);
      setTimeout(() => inputs.current[0]?.focus(), 100);
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (resendCD > 0) return;
    setLoading(true);
    try {
      await api.post('/auth/request-otp', { email });
      toast.success('New OTP sent!');
      setOtp(['','','','','','']);
      startCountdown();
      setTimeout(() => inputs.current[0]?.focus(), 100);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh', background:'#0f1117', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#1a1d27', border:'1px solid #2a2d3a', borderRadius:18, padding:'44px 40px', width:'100%', maxWidth:420 }}>

        {/* Logo */}
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:32 }}>
          <div style={{ width:40, height:40, background:'linear-gradient(135deg,#6c63ff,#4ecdc4)', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:12, color:'#fff' }}>HR</div>
          <span style={{ fontSize:18, fontWeight:700, color:'#fff' }}>HR Portal</span>
        </div>

        {step === 'email' ? (
          <>
            <h1 style={{ fontSize:24, fontWeight:700, color:'#fff', marginBottom:6 }}>Sign in</h1>
            <p style={{ color:'#6b7280', fontSize:13, marginBottom:28 }}>Enter your email to receive a one-time password</p>
            <form onSubmit={handleRequestOTP} style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div className="field">
                <label>Email Address</label>
                <input type="email" required autoFocus value={email} onChange={e => setEmail(e.target.value)} placeholder="hr@company.com"/>
              </div>
              <button type="submit" className="btn btn-primary" style={{ width:'100%', justifyContent:'center', padding:12, marginTop:4 }} disabled={loading}>
                {loading ? 'Sending OTP…' : 'Send OTP →'}
              </button>
            </form>
          </>
        ) : (
          <>
            <button onClick={() => setStep('email')} style={{ background:'none', border:'none', color:'#6c63ff', cursor:'pointer', fontSize:13, padding:0, marginBottom:20, display:'flex', alignItems:'center', gap:4 }}>
              ← Back
            </button>
            <h1 style={{ fontSize:22, fontWeight:700, color:'#fff', marginBottom:6 }}>Enter OTP</h1>
            <p style={{ color:'#6b7280', fontSize:13, marginBottom:6 }}>
              We sent a 6-digit code to
            </p>
            <p style={{ color:'#6c63ff', fontSize:13, fontWeight:600, marginBottom:28 }}>{email}</p>

            {/* 6-box OTP input */}
            <div style={{ display:'flex', gap:10, justifyContent:'center', marginBottom:28 }}>
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  ref={el => inputs.current[idx] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOTPInput(idx, e.target.value)}
                  onKeyDown={e => handleOTPKeyDown(idx, e)}
                  style={{
                    width:48, height:56, textAlign:'center', fontSize:22, fontWeight:700,
                    background:'#0f1117', border:`2px solid ${digit ? '#6c63ff' : '#2a2d3a'}`,
                    borderRadius:10, color:'#fff', outline:'none', fontFamily:'monospace',
                    transition:'border-color .15s'
                  }}
                />
              ))}
            </div>

            <button
              className="btn btn-primary"
              style={{ width:'100%', justifyContent:'center', padding:12 }}
              disabled={loading || otp.join('').length < 6}
              onClick={() => handleVerifyOTP()}
            >
              {loading ? 'Verifying…' : 'Verify OTP & Login'}
            </button>

            <div style={{ textAlign:'center', marginTop:18, fontSize:13, color:'#6b7280' }}>
              Didn't receive it?{' '}
              <button
                onClick={handleResend}
                disabled={resendCD > 0}
                style={{ background:'none', border:'none', color: resendCD > 0 ? '#4b5563' : '#6c63ff', cursor: resendCD > 0 ? 'default' : 'pointer', fontSize:13, fontWeight:600, padding:0 }}
              >
                {resendCD > 0 ? `Resend in ${resendCD}s` : 'Resend OTP'}
              </button>
            </div>

            <p style={{ textAlign:'center', fontSize:11.5, color:'#4b5563', marginTop:12 }}>
              OTP expires in 10 minutes
            </p>
          </>
        )}

      </div>
    </div>
  );
}